/**
 * youtube-tracker.js — CakeMiami YouTube Video Tracking
 * Tracks video_play, video_progress (25/50/75%), video_complete via postMessage.
 * Uses MutationObserver to detect dynamically inserted YouTube iframes (deferred-media).
 */
(function () {
  'use strict';

  var trackers = [];

  function getVideoTitle(iframe) {
    var title = iframe.getAttribute('title');
    if (title && title !== '') return title;

    var el = iframe;
    while (el && el !== document.body) {
      el = el.parentElement;
      if (el) {
        var heading = el.querySelector('h1, h2, h3, h4, h5, h6');
        if (heading) return (heading.textContent || '').replace(/\s+/g, ' ').trim();
      }
    }
    return 'Untitled';
  }

  function initTracker(iframe) {
    // Prevent double-tracking
    if (iframe._ytTracked) return;
    iframe._ytTracked = true;

    var videoTitle = getVideoTitle(iframe);
    var videoUrl = iframe.src || '';
    var firedMilestones = {};
    var pollInterval = null;
    var hasPlayed = false;

    function fireMilestone(percent) {
      if (firedMilestones[percent]) return;
      firedMilestones[percent] = true;

      if (percent === 100) {
        window.dataLayer.push({
          event: 'video_complete',
          video_title: videoTitle,
          video_url: videoUrl,
          video_percent: 100,
          page_path: window.location.pathname
        });
      } else {
        window.dataLayer.push({
          event: 'video_progress',
          video_title: videoTitle,
          video_url: videoUrl,
          video_percent: percent,
          page_path: window.location.pathname
        });
      }
    }

    function checkProgress(info) {
      if (!info.currentTime || !info.duration || info.duration === 0) return;
      var percent = (info.currentTime / info.duration) * 100;
      var milestones = [25, 50, 75];
      for (var i = 0; i < milestones.length; i++) {
        if (percent >= milestones[i]) fireMilestone(milestones[i]);
      }
      // Detect near-completion from infoDelivery since onStateChange ENDED
      // may not fire reliably (especially with autoplay embeds)
      if (info.duration - info.currentTime < 1.5) fireMilestone(100);
    }

    function sendCommand(func) {
      var msg = JSON.stringify({ event: 'command', func: func });
      var origin = (iframe.src || '').indexOf('youtube-nocookie.com') !== -1
        ? 'https://www.youtube-nocookie.com'
        : 'https://www.youtube.com';
      try { iframe.contentWindow.postMessage(msg, origin); } catch (e) {}
    }

    function startPolling() {
      if (pollInterval) return;
      pollInterval = setInterval(function () {
        sendCommand('getCurrentTime');
        sendCommand('getDuration');
      }, 500);
    }

    function stopPolling() {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    }

    function handleMessage(event) {
      if (event.origin !== 'https://www.youtube.com' && event.origin !== 'https://www.youtube-nocookie.com') return;

      try {
        var data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.event === 'onStateChange') {
          var state = data.info;
          if (state === 1) { // PLAYING
            if (!hasPlayed) {
              hasPlayed = true;
              window.dataLayer.push({
                event: 'video_play',
                video_title: videoTitle,
                video_url: videoUrl,
                page_path: window.location.pathname
              });
            }
            startPolling();
          } else if (state === 2 || state === 0) { // PAUSED or ENDED
            stopPolling();
          }
          if (state === 0) { // ENDED
            fireMilestone(100);
          }
        }

        if (data.event === 'infoDelivery' && data.info) {
          // If we receive playback data but missed the initial onStateChange (autoplay),
          // treat it as video_play
          if (!hasPlayed && data.info.currentTime > 0) {
            hasPlayed = true;
            window.dataLayer.push({
              event: 'video_play',
              video_title: videoTitle,
              video_url: videoUrl,
              page_path: window.location.pathname
            });
            startPolling();
          }
          checkProgress(data.info);
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    }

    window.addEventListener('message', handleMessage);

    // Tell YouTube iframe to start sending state change events.
    // Must use {event: "listening"} (not sendCommand which wraps as {event: "command"}).
    // Retry because the YT player inside the iframe may not be ready immediately.
    // Use '*' as target origin since the embed may lack an origin= param.
    var listenMsg = JSON.stringify({ event: 'listening' });
    var listenAttempts = 0;
    var listenInterval = setInterval(function () {
      try { iframe.contentWindow.postMessage(listenMsg, '*'); } catch (e) {}
      listenAttempts++;
      if (listenAttempts >= 20 || hasPlayed) clearInterval(listenInterval);
    }, 250);

    trackers.push({
      iframe: iframe,
      destroy: function () {
        stopPolling();
        window.removeEventListener('message', handleMessage);
      }
    });
  }

  function scanForIframes(root) {
    var iframes = root.querySelectorAll('iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"]');
    for (var i = 0; i < iframes.length; i++) {
      if (/enablejsapi=1/.test(iframes[i].src)) {
        initTracker(iframes[i]);
      }
    }
  }

  function setup() {
    // Scan for any existing YouTube iframes
    scanForIframes(document);

    // Watch for dynamically inserted YouTube iframes (deferred-media pattern)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IFRAME') {
            var src = node.src || '';
            if (/youtube\.com\/embed|youtube-nocookie\.com\/embed/.test(src) && /enablejsapi=1/.test(src)) {
              initTracker(node);
            }
          } else {
            scanForIframes(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
